import React, { useEffect, useMemo } from 'react';
import { motion, useSpring, useTransform, type MotionValue } from 'motion/react';

import './Counter.css';

/**
 * Odometer-style rolling counter (React Bits, TS port).
 * Holds the previous value and springs each digit column to the next one,
 * so number changes are visible instead of a silent text swap.
 *
 * Extensions over the upstream component:
 * - ',' is a valid place (static thousands separator).
 * - `decimalStyle` lets the decimal point + decimal digits be styled
 *   smaller/faded (matches the header Credits treatment).
 * - Visual digits are aria-hidden; render the real value via `.sr-only`
 *   next to the counter for screen readers.
 */

type Place = number | '.' | ',';

interface CounterProps {
  value: number;
  /** Digit places left→right, e.g. [1000, 100, 10, 1, '.', 0.1, 0.01]. Derived from `value` when omitted. */
  places?: Place[];
  fontSize?: number;
  padding?: number;
  gap?: number;
  textColor?: string;
  fontWeight?: number | string;
  /** Extra style for the decimal point + decimal-place digits. */
  decimalStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  counterStyle?: React.CSSProperties;
  digitStyle?: React.CSSProperties;
  gradientHeight?: number;
  gradientFrom?: string;
  gradientTo?: string;
}

/** Avoid float dust (12.999999997) flipping the wrong digit while a spring settles. */
function normalizeNearInteger(num: number): number {
  const nearest = Math.round(num);
  const tolerance = 1e-9 * Math.max(1, Math.abs(num));
  return Math.abs(num - nearest) < tolerance ? nearest : num;
}

function getValueRoundedToPlace(value: number, place: number): number {
  return Math.floor(normalizeNearInteger(value / place));
}

/** Places for a value: grouped integer digits + up to 2 decimals when fractional. */
function getPlacesForValue(value: number): Place[] {
  const maxDecimals = Number.isInteger(value) ? 0 : 2;
  const [intPart, decPart] = value.toFixed(maxDecimals).split('.');
  const places: Place[] = [];
  intPart.split('').forEach((_, i) => {
    places.push(10 ** (intPart.length - 1 - i));
    const remaining = intPart.length - 1 - i;
    if (remaining > 0 && remaining % 3 === 0) places.push(',');
  });
  if (decPart) {
    places.push('.');
    decPart.split('').forEach((_, i) => places.push(10 ** -(i + 1)));
  }
  return places;
}

function RollingNumber({ mv, number, height }: { mv: MotionValue<number>; number: number; height: number }) {
  const y = useTransform(mv, (latest) => {
    const placeValue = latest % 10;
    const offset = (10 + number - placeValue) % 10;
    let memo = offset * height;
    // Wrap around the shortest path so 9→0 rolls forward, not backwards.
    if (offset > 5) memo -= 10 * height;
    return memo;
  });
  return (
    <motion.span className="counter-number" style={{ y }}>
      {number}
    </motion.span>
  );
}

function Digit({
  place,
  value,
  height,
  digitStyle,
  decimalStyle,
}: {
  place: Place;
  value: number;
  height: number;
  digitStyle?: React.CSSProperties;
  decimalStyle?: React.CSSProperties;
}) {
  const isDecimal = typeof place === 'string' && place !== ',';
  const valueRoundedToPlace = typeof place === 'number' ? getValueRoundedToPlace(value, place) : 0;
  const animatedValue = useSpring(valueRoundedToPlace);

  useEffect(() => {
    if (typeof place === 'number') animatedValue.set(valueRoundedToPlace);
  }, [animatedValue, valueRoundedToPlace, place]);

  const style = typeof place === 'number' && place < 1 ? { ...digitStyle, ...decimalStyle } : digitStyle;

  if (typeof place === 'string') {
    return (
      <span className="counter-digit" style={{ height, ...style, width: 'fit-content' }}>
        {place}
      </span>
    );
  }

  return (
    <span className="counter-digit" style={{ height, ...style }}>
      {Array.from({ length: 10 }, (_, i) => (
        <RollingNumber key={i} mv={animatedValue} number={i} height={height} />
      ))}
    </span>
  );
}

export default function Counter({
  value: rawValue,
  places,
  fontSize = 100,
  padding = 0,
  gap = 8,
  textColor = 'inherit',
  fontWeight = 'inherit',
  decimalStyle,
  containerStyle,
  counterStyle,
  digitStyle,
  gradientHeight = 16,
  gradientFrom = 'black',
  gradientTo = 'transparent',
}: CounterProps) {
  // Coerce defensively: callers pass values typed as `number` but some API
  // payloads arrive as strings (toLocaleString hid this for years).
  const value = Number(rawValue);
  const isFiniteValue = Number.isFinite(value);
  const height = fontSize + padding;
  const resolvedPlaces = useMemo(
    () => places ?? (isFiniteValue ? getPlacesForValue(value) : [1]),
    [places, value, isFiniteValue]
  );

  const mergedCounterStyle: React.CSSProperties = {
    fontSize,
    gap,
    borderRadius: 0,
    paddingLeft: 0,
    paddingRight: 0,
    color: textColor,
    fontWeight,
    direction: 'ltr',
    ...counterStyle,
  };

  const topGradientStyle: React.CSSProperties = {
    height: gradientHeight,
    background: `linear-gradient(to bottom, ${gradientFrom}, ${gradientTo})`,
  };
  const bottomGradientStyle: React.CSSProperties = {
    height: gradientHeight,
    background: `linear-gradient(to top, ${gradientFrom}, ${gradientTo})`,
  };

  return (
    <span className="counter-container" style={containerStyle} aria-hidden="true">
      <span className="counter-counter" style={mergedCounterStyle}>
        {isFiniteValue ? (
          resolvedPlaces.map((place, index) => (
            <Digit
              key={`${place}-${index}`}
              place={place}
              value={value}
              height={height}
              digitStyle={digitStyle}
              decimalStyle={decimalStyle}
            />
          ))
        ) : (
          <span style={{ height, display: 'inline-flex', alignItems: 'center' }}>—</span>
        )}
      </span>
      {gradientHeight > 0 && (
        <span className="gradient-container">
          <span className="top-gradient" style={topGradientStyle} />
          <span className="bottom-gradient" style={bottomGradientStyle} />
        </span>
      )}
    </span>
  );
}
